import { Link } from 'react-router-dom'
import { ArrowRight, Calendar, Sparkles, Shield } from 'lucide-react'

interface CTAProps {
  variant?: 'default' | 'dark' | 'light' | 'gradient'
  headline?: string
  subheadline?: string
  primaryButtonText?: string
  primaryButtonLink?: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
  showTrustBadge?: boolean
}

export default function CTA({
  variant = 'default',
  headline = 'Ready to Transform Your Compliance Program?',
  subheadline = 'Join hundreds of enterprise teams who have simplified compliance with Clarantis. Start your journey to continuous compliance today.',
  primaryButtonText = 'Request a Demo',
  primaryButtonLink = '/contact',
  secondaryButtonText = 'See How It Works',
  secondaryButtonLink = '/features',
  showTrustBadge = true,
}: CTAProps) {
  // Variant-specific styles
  const variantStyles = {
    default: {
      section: 'bg-primary-700',
      headline: 'text-white',
      subheadline: 'text-primary-100',
      primaryButton: 'bg-accent-teal text-primary-700 hover:bg-accent-mint shadow-lg focus:ring-accent-teal focus:ring-offset-primary-700',
      secondaryButton: 'bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm focus:ring-white focus:ring-offset-primary-700',
      badge: 'bg-white/10 text-primary-100',
      badgeIcon: 'text-accent-teal',
    },
    dark: {
      section: 'bg-gradient-to-br from-primary-800 via-primary-700 to-primary-800',
      headline: 'text-white',
      subheadline: 'text-primary-100',
      primaryButton: 'bg-accent-teal text-primary-700 hover:bg-accent-mint shadow-lg focus:ring-accent-teal focus:ring-offset-primary-800',
      secondaryButton: 'bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-sm focus:ring-white focus:ring-offset-primary-800',
      badge: 'bg-white/10 text-primary-100',
      badgeIcon: 'text-accent-teal',
    },
    light: {
      section: 'bg-navy-50',
      headline: 'text-primary-700',
      subheadline: 'text-navy-500',
      primaryButton: 'bg-primary-700 text-white hover:bg-primary-600 shadow-lg focus:ring-primary-700 focus:ring-offset-navy-50',
      secondaryButton: 'bg-white text-primary-700 border-navy-200 hover:bg-navy-100 shadow-sm focus:ring-primary-700 focus:ring-offset-navy-50',
      badge: 'bg-primary-700/10 text-primary-700',
      badgeIcon: 'text-accent-teal',
    },
    gradient: {
      section: 'bg-gradient-to-r from-primary-700 via-primary-600 to-primary-700',
      headline: 'text-white',
      subheadline: 'text-primary-100',
      primaryButton: 'bg-white text-primary-700 hover:bg-navy-50 shadow-lg focus:ring-white focus:ring-offset-primary-700',
      secondaryButton: 'bg-accent-teal/20 text-accent-teal border-accent-teal/30 hover:bg-accent-teal/30 backdrop-blur-sm focus:ring-accent-teal focus:ring-offset-primary-700',
      badge: 'bg-accent-teal/20 text-accent-teal',
      badgeIcon: 'text-accent-mint',
    },
  }

  const styles = variantStyles[variant]

  return (
    <section className={`relative overflow-hidden py-16 sm:py-24 ${styles.section}`}>
      {/* Background pattern for dark variants */}
      {(variant === 'default' || variant === 'dark' || variant === 'gradient') && (
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
      )}

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          {/* Trust badge */}
          {showTrustBadge && (
            <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium mb-6 ${styles.badge}`}>
              <Shield className={`h-4 w-4 ${styles.badgeIcon}`} aria-hidden="true" />
              <span>Trusted by 500+ Enterprise Teams</span>
            </div>
          )}

          {/* Headline */}
          <h2 className={`text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl ${styles.headline}`}>
            {headline}
          </h2>

          {/* Subheadline */}
          <p className={`mt-6 text-lg leading-8 sm:text-xl ${styles.subheadline}`}>
            {subheadline}
          </p>

          {/* Feature highlights */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 sm:gap-8">
            <div className={`flex items-center gap-2 text-sm ${variant === 'light' ? 'text-navy-500' : 'text-primary-100'}`}>
              <Sparkles className={`h-4 w-4 ${styles.badgeIcon}`} aria-hidden="true" />
              <span>No credit card required</span>
            </div>
            <div className={`flex items-center gap-2 text-sm ${variant === 'light' ? 'text-navy-500' : 'text-primary-100'}`}>
              <Calendar className={`h-4 w-4 ${styles.badgeIcon}`} aria-hidden="true" />
              <span>Get started in minutes</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center sm:gap-6">
            <Link
              to={primaryButtonLink}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-base font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.primaryButton}`}
            >
              {primaryButtonText}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
            {secondaryButtonText && (
              <Link
                to={secondaryButtonLink}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-8 py-4 text-base font-semibold transition-colors border focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.secondaryButton}`}
              >
                {secondaryButtonText}
              </Link>
            )}
          </div>

          {/* Additional trust indicators */}
          <div className={`mt-12 text-sm ${variant === 'light' ? 'text-navy-400' : 'text-primary-200'}`}>
            <p>
              Join industry leaders from Financial Services, Healthcare, Technology, and Manufacturing
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// Pre-configured CTA variants for common use cases
export function CTADefault() {
  return <CTA variant="default" />
}

export function CTADark() {
  return (
    <CTA
      variant="dark"
      headline="Take Control of Your Compliance Journey"
      subheadline="See how Clarantis can help you achieve continuous compliance with less effort. Schedule a personalized demo with our team."
    />
  )
}

export function CTALight() {
  return (
    <CTA
      variant="light"
      headline="Start Your Free Trial Today"
      subheadline="Experience the power of modern GRC. No commitment required—see results in your first week."
      primaryButtonText="Start Free Trial"
      secondaryButtonText="Talk to Sales"
      secondaryButtonLink="/contact"
    />
  )
}

export function CTAGradient() {
  return (
    <CTA
      variant="gradient"
      headline="The Future of GRC is Here"
      subheadline="AI-powered compliance automation that scales with your organization. Ready to see what's possible?"
      primaryButtonText="Get Started"
      showTrustBadge={false}
    />
  )
}

export function CTAMinimal() {
  return (
    <CTA
      variant="default"
      headline="Questions? Let's Talk."
      subheadline="Our team of compliance experts is here to help you find the right solution for your organization."
      primaryButtonText="Contact Us"
      primaryButtonLink="/contact"
      secondaryButtonText=""
      showTrustBadge={false}
    />
  )
}
